import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { selfServiceApi } from "./api";
import {
  CreateFAQPayload,
  CreateTipPayload,
  UpdateFAQPayload,
  UpdateFAQStatusPayload,
  UpdateTipPayload,
  UpdateTipStatusPayload,
  CreateServiceCenterPayload,
  UpdateServiceCenterStatusPayload,
  UpdateServiceCenterPayload,
} from "./types";

export const useFaqList = (params: {
  remote_utility_id: any;
  is_pagination_required: any;
  page: number;
  limit: number;
  show_inactive?: any;
  search_data?: string;
  faq_category?: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.selfService.faqList(params),
    () => selfServiceApi.getFaqList(params),
  );
};

export const useTipsData = (params: {
  remote_utility_id: any;
  is_pagination_required: any;
  page: any;
  limit: any;
  show_inactive: any;
  search_data?: string;
  utility_service?: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.selfService.tipsData(params),
    () => selfServiceApi.getTipsData(params),
  );
};

export const useSearchTips = (params: {
  remote_utility_id: any;
  search_data: string;
  search_field?: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.selfService.searchTips(params),
    () => selfServiceApi.searchTips(params),
    {
      enabled: !!params.search_data && params.search_data.trim().length > 0, // Only run query if search_data is provided and not empty
    },
  );
};

export const useSearchFAQ = (params: {
  remote_utility_id: any;
  search_data: string;
  search_field?: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.selfService.searchFAQ(params),
    () => selfServiceApi.searchFAQ(params),
    {
      enabled: !!params.search_data && params.search_data.trim().length > 0, // Only run query if search_data is provided and not empty
    },
  );
};
export const useDashboardDetails = (params: { remote_utility_id: any }) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.selfService.dashboardDetails(params),
    () => selfServiceApi.getDashboardDetails(params),
  );
};

export const useFaqCategory = (params: { remote_utility_id: any }) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.selfService.faqCategory(params),
    () => selfServiceApi.getFaqCategory(params),
  );
};

// Service Center Hooks
export const useServiceCenterList = (params: {
  remote_utility_id: any;
  show_inactive?: boolean;
  page: number;
  limit: number;
  search_data?: string;
  type?: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.selfService.serviceCenterList(params),
    () => selfServiceApi.getServiceCenterList(params),
  );
};

export const useCreateServiceCenter = () => {
  return useSmartMutation((payload: CreateServiceCenterPayload) =>
    selfServiceApi.createServiceCenter(payload),
  );
};

export const useUpdateServiceCenterStatus = () => {
  return useSmartMutation(
    ({
      id,
      payload,
    }: {
      id: string | number;
      payload: UpdateServiceCenterStatusPayload;
    }) => selfServiceApi.updateServiceCenterStatus(id, payload),
  );
};

export const useUpdateServiceCenter = () => {
  return useSmartMutation(
    ({ id, payload }: { id: string; payload: UpdateServiceCenterPayload }) =>
      selfServiceApi.updateServiceCenter(id, payload),
  );
};

// Existing FAQ and Tips Hooks
export const useAddFaqs = () => {
  return useSmartMutation((payload: CreateFAQPayload) =>
    selfServiceApi.addFaqs(payload),
  );
};

export const useAddTip = () => {
  return useSmartMutation((payload: CreateTipPayload) =>
    selfServiceApi.addTips(payload),
  );
};

export const useUpdateTip = () => {
  return useSmartMutation((payload: UpdateTipPayload) =>
    selfServiceApi.updateTip(payload),
  );
};

export const useUpdateFaqs = () => {
  return useSmartMutation((payload: UpdateFAQPayload) =>
    selfServiceApi.updateFaqs(payload),
  );
};

export const useUpdateFaqStatus = () => {
  return useSmartMutation((payload: UpdateFAQStatusPayload) =>
    selfServiceApi.updateFaqStatus(payload),
  );
};

export const useUpdateTipStatus = () => {
  return useSmartMutation((payload: UpdateTipStatusPayload) =>
    selfServiceApi.updateTipStatus(payload),
  );
};

export const useCity = (params: { remote_utility_id: any }) => {
  return useSmartQuery(QueryKeyFactory.module.cx.selfService.city(params), () =>
    selfServiceApi.getCity(params),
  );
};

export const useArea = (params: {
  remote_utility_id: any;
  code_list: string | string[];
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.selfService.area(params),
    () => selfServiceApi.getArea(params),
    {
      enabled: Array.isArray(params.code_list) ? params.code_list.length > 0 : !!params.code_list,
    },
  );
};

export const useSubArea = (params: {
  remote_utility_id: any;
  code_list: string | string[];
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.selfService.subArea(params),
    () => selfServiceApi.getSubArea(params),
    {
      enabled: Array.isArray(params.code_list) ? params.code_list.length > 0 : !!params.code_list,
    },
  );
};

export const usePremise = (params: {
  remote_utility_id: any;
  code_list: string | string[];
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.selfService.premise(params),
    () => selfServiceApi.getPremise(params),
    {
      enabled: Array.isArray(params.code_list) ? params.code_list.length > 0 : !!params.code_list,
    },
  );
};

export const useServiceCenters = (params: { remote_utility_id: any }) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.selfService.serviceCenters(params),
    () => selfServiceApi.getServiceCenters(params),
  );
};

  // useDeleteFaq.ts
  export const useDeleteFaq = () => {
    return useSmartMutation(
      (params: { remote_utility_id: any; code: any; type:any }) =>
        selfServiceApi.deleteFaq(params),
    );
  };

  export const useDeleteServiceCenter = () => {
    return useSmartMutation(
      (params: { id: string}) =>
        selfServiceApi.deleteServiceCenter(params),
    );
  };
  export const useAddSelfServieBranding = () => {
    return useSmartMutation(
      (payload: any) => selfServiceApi.addSelfServieBranding(payload)
    );
  };
  export const useGetSelfServieBranding = (remoteUtilityId: string) => {
    return useSmartQuery(
      QueryKeyFactory.module.cx.selfService.selfServieBranding(remoteUtilityId),
      () => selfServiceApi.getSelfServieBranding(remoteUtilityId)
    );
  };
  
  export const useUpdateSelfServieBranding = () => {
    return useSmartMutation(
      ({ id, payload }: { id: string | number; payload: any }) => selfServiceApi.updateSelfServieBranding(id, payload)
    );
  };

  export const useGetWidgets = (params: { remote_utility_id: any }) => {
    return useSmartQuery(
      ["selfService", "widgets", params],
      () => selfServiceApi.getWidgets(params)
    );
  };
  
  export const useUpdateWidgets = () => {
    return useSmartMutation(
      (payload: any) => selfServiceApi.updateWidgets(payload)
    );
  };
  
  export const useGetGuestPortalBranding = (params: { remote_utility_id: any }) => {
    return useSmartQuery(
      ["selfService", "guestPortalBranding", params],
      () => selfServiceApi.getGuestPortalBranding(params)
    );
  };

  export const useUpdateGuestPortalBranding = () => {
    return useSmartMutation(
      (payload: FormData) => selfServiceApi.updateGuestPortalBranding(payload)
    );
  };
  
  // Trigger Vite rebuild
